/** @jsxImportSource @emotion/react */
import React from "react";
import Select from "react-select";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import { makeStyles } from "@material-ui/core/styles";
import {
    GYSectionCard, GYSectionHeader, GYSectionBody,
    GYFormRow, GYFormGroup, GYLabel, GYInput, GYSelect, GYTextarea,
    GYCheckBox, GYCheckRow,
    GYCycleCard, GYCycleStat, GYCycleLabel, GYCycleValue,
    GYGPALPill,
    GYAlertBanner, GYAlertIcon, GYAlertContent, GYAlertTitle, GYAlertDetail,
    GYActionBar, GYPrimaryBtn, GYSecondaryBtn,
    GYNavBar,
    GYRequiredStar, GYFieldError,
} from "./GynaeConsultationStyles";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";
import TextField from "../../../Utils/FormInputs/TextField";
import SelectInput, { CustomSelect } from "../../../Utils/FormInputs/SelectInput";


/* ═══════════════════════════════════════════════════════════════
   GynaeConsultationForm — Gynaecological Consultation Form (Task 18)
   Wizard-style sections: History → Examination
   (Diagnosis and Treatment/Plan are handled via the right sidebar)
   ═══════════════════════════════════════════════════════════════ */

const SECTION_META = {
    history: { num: 1, label: "History", icon: "" },
    examination: { num: 2, label: "Examination", icon: "" },
};

/* ─── Error border style applied to inputs with validation errors ─── */
const errorBorderStyle = { borderColor: "#d32f2f", boxShadow: "0 0 0 1px #d32f2f" };

/* ─── react-select multi styles matching GY theme ─── */
const multiSelectStyles = {
    control: function (base) {
        return Object.assign({}, base, {
            fontSize: "0.85rem",
            borderRadius: "4px",
            borderColor: "#ccc",
            minHeight: "34px",
            boxShadow: "none",
            "&:hover": { borderColor: "#005a8b" },
        });
    },
    option: function (base, state) {
        return Object.assign({}, base, {
            fontSize: "0.85rem",
            backgroundColor: state.isSelected ? "#005a8b" : state.isFocused ? "#e8f0fe" : "#fff",
            color: state.isSelected ? "#fff" : "#333",
            cursor: "pointer",
        });
    },
    multiValue: function (base) {
        return Object.assign({}, base, {
            backgroundColor: "#005a8b18",
            borderRadius: "3px",
        });
    },
    multiValueLabel: function (base) {
        return Object.assign({}, base, { color: "#005a8b", fontSize: "0.78rem", fontWeight: 600 });
    },
    multiValueRemove: function (base) {
        return Object.assign({}, base, {
            color: "#005a8b",
            ":hover": { backgroundColor: "#005a8b", color: "#fff" },
        });
    },
    menuPortal: function (base) { return Object.assign({}, base, { zIndex: 9999 }); },
    placeholder: function (base) { return Object.assign({}, base, { fontSize: "0.85rem", color: "#bbb" }); },
};

/* ─── Convert plain string array ↔ react-select option objects ─── */
const toOptions = function (values, optionsList) {
    if (!values || !values.length) return [];
    return optionsList.filter(function (o) { return values.indexOf(o.value) >= 0; });
};

const onMultiChange = function (name, handleChange) {
    return function (selected) {
        const values = (selected || []).map(function (o) { return o.value; });
        handleChange({ target: { name: name, value: values } });
    };
};

const useStepperStyles = makeStyles(function () {
    return {
        stepper: {
            padding: "12px 0",
            backgroundColor: "transparent",
            "& .MuiStepLabel-label": { fontSize: "12px" },
            "& .MuiStepIcon-root.MuiStepIcon-active": { color: "#005a8b" },
            "& .MuiStepIcon-root.MuiStepIcon-completed": { color: "#4caf50" },
        },
    };
});

export const GynaeConsultationForm = function (props) {
    const gyn = props.gyn;
    const form = gyn.form;
    const handleChange = gyn.handleChange;
    const activeSection = gyn.activeSection;
    const validationErrors = gyn.validationErrors || {};
    const REQUIRED_FIELDS = gyn.REQUIRED_FIELDS || {};

    const classes = useStepperStyles();
    const activeStepIndex = gyn.FORM_SECTIONS.indexOf(activeSection);

    /* ─── Check if section is "completed" (has key data) ─── */
    const isSectionCompleted = function (section) {
        if (section === "history") return !!form.menarche_age || !!form.lmp;
        if (section === "examination") return !!form.presenting_complaint || Object.keys(form.examination).length > 0;
        return false;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* ═══ Stepper Navigation ═══ */}
            <Stepper
                activeStep={activeStepIndex}
                alternativeLabel
                className={classes.stepper}
            >
                {gyn.FORM_SECTIONS.map(function (sec, idx) {
                    return (
                        <Step
                            key={sec}
                            completed={isSectionCompleted(sec) && idx < activeStepIndex}
                            onClick={function () { gyn.setActiveSection(sec); }}
                            style={{ cursor: "pointer" }}
                        >
                            <StepLabel>{SECTION_META[sec].label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {/* ═══ SECTION 1: HISTORY ═══ */}
            {activeSection === "history" && (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {/* ─── Menstrual History ─── */}
                            <GYFormRow cols="1fr 1fr 1fr 1fr">
                                <Form.Group>
                                    <Label name='Age at Menarche'/>
                                    <TextField
                                        name="menarche_age"
                                        type="number"
                                        value={form.menarche_age}
                                        onChange={handleChange}
                                        placeholder="e.g. 13"
                                        min="8" max="20"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Cycle Length (days)'/>
                                    <TextField
                                        name="cycle_length"
                                        type="number"
                                        value={form.cycle_length}
                                        onChange={handleChange}
                                        placeholder="28"
                                        min="18" max="45"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Period Duration (days)'/>
                                    <TextField
                                        name="period_duration"
                                        type="number"
                                        value={form.period_duration}
                                        onChange={handleChange}
                                        placeholder="5"
                                        min="1" max="14"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Last Menstrual Period' />
                                    <TextField
                                        name="lmp"
                                        type="date"
                                        value={form.lmp}
                                        onChange={handleChange}
                                        style={validationErrors.lmp ? errorBorderStyle : undefined}
                                    />
                                    {validationErrors.lmp && <GYFieldError>{validationErrors.lmp}</GYFieldError>}
                                </Form.Group>
                            </GYFormRow>
                            <GYFormRow cols="1fr 1fr 1fr">
                                <Form.Group>
                                    <Label name='Regularity'/>
                                    <SelectInput options={gyn.MENSTRUAL_REGULARITY.map(function (r) {
                                        return { value: r.value, text: r.label };
                                    })} name="regularity" value={form.regularity} onChange={handleChange}
                                        style={validationErrors.regularity ? errorBorderStyle : undefined} />
                                    {validationErrors.regularity && <GYFieldError>{validationErrors.regularity}</GYFieldError>}
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Flow Amount'/>
                                    <SelectInput options={gyn.MENSTRUAL_FLOW.map(function (f) {
                                        return { value: f.value, text: f.label };
                                    })} name="flow_amount" value={form.flow_amount} onChange={handleChange} />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Dysmenorrhea'/>
                                    <SelectInput options={gyn.DYSMENORRHEA_SEVERITY.map(function (d) {
                                        return { value: d.value, text: d.label };
                                    })} name="dysmenorrhea" value={form.dysmenorrhea} onChange={handleChange} />
                                </Form.Group>
                            </GYFormRow>

                            {/* Cycle info card */}
                            {gyn.cycleInfo && (
                                <GYCycleCard>
                                    <GYCycleStat>
                                        <GYCycleLabel>Days Since LMP</GYCycleLabel>
                                        <GYCycleValue>{gyn.cycleInfo.daysSinceLMP}</GYCycleValue>
                                    </GYCycleStat>
                                    <GYCycleStat>
                                        <GYCycleLabel>Cycle Day</GYCycleLabel>
                                        <GYCycleValue>{gyn.cycleInfo.cycleDay}</GYCycleValue>
                                    </GYCycleStat>
                                    <GYCycleStat>
                                        <GYCycleLabel>Expected Next Period</GYCycleLabel>
                                        <GYCycleValue>{gyn.cycleInfo.expectedNextPeriod}</GYCycleValue>
                                    </GYCycleStat>
                                    {gyn.cycleInfo.isLate && (
                                        <GYCycleStat>
                                            <GYCycleLabel>Days Late</GYCycleLabel>
                                            <GYCycleValue alert>{gyn.cycleInfo.daysLate} days</GYCycleValue>
                                        </GYCycleStat>
                                    )}
                                </GYCycleCard>
                            )}

                            {gyn.cycleInfo && gyn.cycleInfo.isLate && gyn.cycleInfo.daysLate > 7 && (
                                <GYAlertBanner type="warning">
                                    <GYAlertIcon>!</GYAlertIcon>
                                    <GYAlertContent>
                                        <GYAlertTitle>Period Late by {gyn.cycleInfo.daysLate} Days</GYAlertTitle>
                                        <GYAlertDetail>Consider pregnancy test if sexually active.</GYAlertDetail>
                                    </GYAlertContent>
                                </GYAlertBanner>
                            )}

                            <GYFormRow cols="1fr 1fr">
                                <GYCheckRow checked={form.intermenstrual_bleeding}>
                                    <GYCheckBox
                                        type="checkbox"
                                        name="intermenstrual_bleeding"
                                        checked={form.intermenstrual_bleeding}
                                        onChange={handleChange}
                                    />
                                    Intermenstrual Bleeding
                                </GYCheckRow>
                                <GYCheckRow checked={form.post_coital_bleeding}>
                                    <GYCheckBox
                                        type="checkbox"
                                        name="post_coital_bleeding"
                                        checked={form.post_coital_bleeding}
                                        onChange={handleChange}
                                    />
                                    Post-Coital Bleeding
                                </GYCheckRow>
                            </GYFormRow>

                    {/* ─── Obstetric Summary (GPAL) ─── */}
                            
                            <GYFormRow cols="1fr 1fr 1fr 1fr">
                                <Form.Group>
                                    <Label name='Gravida'/>
                                    <TextField
                                        name="gravida"
                                        type="number"
                                        value={form.gravida}
                                        onChange={handleChange}
                                        placeholder="Total pregnancies"
                                        min="0"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Para'/>
                                    <TextField
                                        name="para"
                                        type="number"
                                        value={form.para}
                                        onChange={handleChange}
                                        placeholder="Deliveries ≥28wk"
                                        min="0"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Abortions'/>
                                    <TextField
                                        name="abortions"
                                        type="number"
                                        value={form.abortions}
                                        onChange={handleChange}
                                        placeholder="Losses <28wk"
                                        min="0"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Living Children'/>
                                    <TextField
                                        name="living_children"
                                        type="number"
                                        value={form.living_children}
                                        onChange={handleChange}
                                        placeholder="Living"
                                        min="0"
                                    />
                                </Form.Group>
                            </GYFormRow>

                    {/* ─── Sexual History ─── */}
                            <GYFormRow cols="2fr 1fr">
                                <Form.Group>
                                    <Label name='Sexual History'/>
                                    <CustomSelect
                                        isMulti
                                        options={gyn.SEXUAL_HISTORY_OPTIONS}
                                        value={toOptions(form.sexual_history, gyn.SEXUAL_HISTORY_OPTIONS)}
                                        onChange={onMultiChange("sexual_history", handleChange)}
                                        menuPortalTarget={document.body}
                                        placeholder="Select all that apply..."
                                        closeMenuOnSelect={false}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name='Age at First Intercourse'/>
                                    <TextField
                                        name="age_at_first_intercourse"
                                        type="number"
                                        value={form.age_at_first_intercourse}
                                        onChange={handleChange}
                                        placeholder="e.g. 18"
                                        min="0"
                                    />
                                </Form.Group>
                            </GYFormRow>

                    {/* ─── Contraceptive History ─── */}
                            <GYFormRow cols="1fr 1fr">
                                {/* <Form.Group>
                                    <Label name='Contraceptive Current'/>
                                    <CustomSelect
                                        name="contraceptive_current"
                                        value={form.contraceptive_current}
                                        onChange={handleChange}
                                        style={validationErrors.contraceptive_current ? errorBorderStyle : undefined}
                                        options={gyn.CONTRACEPTIVE_HISTORY_OPTIONS.map(function (o) {
                                            return { value: o.value, text: o.label };
                                        })}
                                        defaultOption="Select current method..."
                                    />
                                      
                                </Form.Group> */}

                                <Form.Group>
                                    <Label name="Contraceptive Current" />

                                    <CustomSelect
                                        options={gyn.CONTRACEPTIVE_HISTORY_OPTIONS}
                                        name="contraceptive_current"
                                        value={
                                            gyn.CONTRACEPTIVE_HISTORY_OPTIONS.find(o => o.value === form.contraceptive_current) || null}

                                        onChange={(option) => handleChange({
                                                target: {
                                                    name: "contraceptive_current",
                                                    value: option ? option.value : "",
                                                    type: "select",
                                                },
                                            })
                                        }
                                        style={validationErrors.contraceptive_current ? errorBorderStyle : undefined}
                                        menuPortalTarget={document.body}
                                        placeholder="Select current method..."
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <Label name='Contraceptive Past'/>
                                    <CustomSelect
                                        isMulti
                                        options={gyn.CONTRACEPTIVE_HISTORY_OPTIONS}
                                        value={toOptions(form.contraceptive_past, gyn.CONTRACEPTIVE_HISTORY_OPTIONS)}
                                        onChange={onMultiChange("contraceptive_past", handleChange)}
                                        menuPortalTarget={document.body}
                                        placeholder="Select past methods..."
                                        closeMenuOnSelect={false}
                                    />
                                </Form.Group>
                            </GYFormRow>

                    {/* ─── Past Gynaecological History ─── */}
                            <Form.Group style={{ marginBottom: "14px" }}>
                                <Label name='Past Gynae History'/>
                                <CustomSelect
                                    isMulti
                                    options={gyn.PAST_GYNAE_CONDITIONS}
                                    value={toOptions(form.past_gynae_history, gyn.PAST_GYNAE_CONDITIONS)}
                                    onChange={onMultiChange("past_gynae_history", handleChange)}
                                    menuPortalTarget={document.body}
                                    placeholder="Select all that apply..."
                                    closeMenuOnSelect={false}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Label name='Past Gynae Details'/>
                                <ResizableTextarea
                                    name="past_gynae_details"
                                    value={form.past_gynae_details}
                                    onChange={handleChange}
                                    placeholder="Any additional details about past gynaecological history, surgeries, treatments..."
                                    rows={3}
                                />
                            </Form.Group>

                    {/* ─── Navigation ─── */}
                    <GYNavBar>
                        <div />
                        <GYPrimaryBtn onClick={gyn.nextSection}>
                            Next: Examination
                        </GYPrimaryBtn>
                    </GYNavBar>
                </div>
            )}

            {/* ═══ SECTION 2: EXAMINATION ═══ */}
            {activeSection === "examination" && (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <GYFormRow cols="1fr 1fr">
                        {/* ─── Left column ─── */}
                        <div>
                            <Form.Group>
                                <Label name='Presenting Complaint'/>
                                <ResizableTextarea
                                    name="presenting_complaint"
                                    value={form.presenting_complaint || ""}
                                    onChange={handleChange}
                                    placeholder="Write presenting complaints..."
                                    rows={4}
                                    submitted={!!validationErrors.presenting_complaint}
                                />
                                {validationErrors.presenting_complaint && <GYFieldError>{validationErrors.presenting_complaint}</GYFieldError>}
                            </Form.Group>
                            <Form.Group>
                                <Label name='Past Medical History'/>
                                <ResizableTextarea
                                    name="past_medical_history"
                                    value={form.past_medical_history || ""}
                                    onChange={handleChange}
                                    placeholder="Write past medical history..."
                                    rows={4}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Label name='Physical Examination'/>
                                <ResizableTextarea
                                    name="physical_examination"
                                    value={form.physical_examination || ""}
                                    onChange={handleChange}
                                    placeholder="Write physical examination findings..."
                                    rows={4}
                                />
                            </Form.Group>
                        </div>
                        {/* ─── Right column ─── */}
                        <div>
                            <Form.Group>
                                <Label name='Past Obstetric History'/>
                                <ResizableTextarea
                                    name="past_obstetric_history"
                                    value={form.past_obstetric_history || ""}
                                    onChange={handleChange}
                                    placeholder="Write past obstetric history..."
                                    rows={4}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Label name='Family History'/>
                                <ResizableTextarea
                                    name="family_history"
                                    value={form.family_history || ""}
                                    onChange={handleChange}
                                    placeholder="Write family history..."
                                    rows={4}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Label name='Notes'/>
                                <ResizableTextarea
                                    name="notes"
                                    value={form.notes || ""}
                                    onChange={handleChange}
                                    placeholder="Write notes..."
                                    rows={4}
                                />
                            </Form.Group>
                        </div>
                    </GYFormRow>

                    {/* ─── Navigation & Action Buttons ─── */}
                    <GYNavBar>
                        <GYSecondaryBtn onClick={gyn.prevSection}>
                            Back: History
                        </GYSecondaryBtn>
                        <GYActionBar>
                            <GYSecondaryBtn onClick={gyn.resetForm}>
                                Reset
                            </GYSecondaryBtn>
                            <GYPrimaryBtn
                                onClick={gyn.saveConsultation}
                                disabled={gyn.saving}
                            >
                                {gyn.saving ? "Saving..." : (gyn.editingRecord ? "Update Consultation" : "Save Consultation")}
                            </GYPrimaryBtn>
                        </GYActionBar>
                    </GYNavBar>
                </div>
            )}
        </div>
    );
};
